import {useEffect, useRef, useState} from 'react';
import MainCard from './components/video/MainCard';
import {PERSONAS} from './config/personas';
import {ACCOUNT_ID} from './api/account';
import {createVideoGeneration} from './api/video';
import {useAccountStatus} from './hooks/useAccountStatus';
import {trackEvent} from './utils/analytics';
import {showError} from './utils/notify';
import Header from './components/layout/Header';
import HeroSection from './components/landing/HeroSection';
import WhyPerfectSection from './components/landing/WhyPerfectSection';
import FiveReasonsSection from './components/landing/FiveReasonsSection';
import Paywall from './components/paywall/Paywall';
import MyContentPage from './components/pages/MyContentPage';
import VideoModal from './components/video/VideoModal';
import Footer from './components/layout/Footer';
import StarReviewSection from './components/landing/StarReviewSection';
import {GENERATION_STATUS_TEXTS} from './config/constants';
import {getPlans} from './api/plans';
import {getPageConfig} from './config/pageConfigs';
import {blogArticles} from './config/blogArticles';

// Main App Component
export default function App({view = 'home'}) {
    // Get page config based on current URL (needed early for preSelectedPersonaId)
    const pageConfig = getPageConfig();

    const {
        credits,
        history,
        subscription,
        linkedTelegramId,
        linkedMaxId,
        refetch: fetchAccountStatus
    } = useAccountStatus();
    const [plans, setPlans] = useState({subscriptions: [], topups: []});
    const isOrders = view === 'orders';
    const [preSelectedPersona, setPreSelectedPersona] = useState(null);
    const [preSelectedText, setPreSelectedText] = useState('');
    const [playingVideo, setPlayingVideo] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatingPersona, setGeneratingPersona] = useState(null);
    const [generatedVideo, setGeneratedVideo] = useState(null);
    const [statusTextIndex, setStatusTextIndex] = useState(0);
    const [trackingGenerationId, setTrackingGenerationId] = useState(null);
    // Use preSelectedPersonaId from config if available
    const [selectedPersonaId, setSelectedPersonaId] = useState(
        pageConfig.preSelectedPersonaId || PERSONAS[0].id
    );
    const [isExampleVideoPlaying, setIsExampleVideoPlaying] = useState(false);
    const [showPostPurchase, setShowPostPurchase] = useState(false);
    const [showPaywall, setShowPaywall] = useState(false);
    const stopExampleVideoRef = useRef(null);
    const mainCardRef = useRef(null);

    // Set meta tags
    useEffect(() => {
        document.title = pageConfig.title;
        document.querySelector('meta[name="description"]')?.setAttribute('content', pageConfig.description);
    }, [pageConfig]);

    // Fetch pricing plans from API
    useEffect(() => {
        getPlans('video').then(data => {
            const mapPlan = (p) => ({
                id: p.id,
                name: p.name,
                videos: p.videos_count,
                price: p.price_rub,
                oldPrice: p.old_price_rub,
                discount: p.discount_percent || null,
                kind: p.kind || 'topup',
                billingPeriod: p.billing_period || null,
            });
            setPlans({
                subscriptions: (data.subscriptions || []).map(mapPlan),
                topups: (data.topups || []).map(mapPlan),
            });
        }).catch(console.error);
    }, []);

    // Cycle through status texts when generating
    useEffect(() => {
        if (!isGenerating) {
            setStatusTextIndex(0);
            return;
        }
        const interval = setInterval(() => {
            setStatusTextIndex(prev => (prev + 1) % GENERATION_STATUS_TEXTS.length);
        }, 10000);
        return () => clearInterval(interval);
    }, [isGenerating]);

    // Track generation status from polled account data
    useEffect(() => {
        const pendingGen = history.find(g => g.status === 'pending' || g.status === 'processing');

        if (pendingGen) {
            if (!isGenerating) {
                const persona = PERSONAS.find(p => p.id === pendingGen.celeb_id);
                setIsGenerating(true);
                setGeneratingPersona(persona);
                setTrackingGenerationId(pendingGen.id);
            }
        } else if (trackingGenerationId) {
            const completedGen = history.find(g => g.id === trackingGenerationId);
            setIsGenerating(false);
            setTrackingGenerationId(null);

            if (completedGen?.status === 'completed' && completedGen?.video_url) {
                setGeneratedVideo(completedGen.video_url);
            } else if (completedGen?.status === 'failed') {
                showError('Ошибка: Не удалось сгенерировать видео');
            }
        }
    }, [history, isGenerating, trackingGenerationId]);

    const handleShareVideo = async (videoUrl) => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Видеопоздравление от ИИ-звезды',
                    text: 'Посмотри мое видеопоздравление от ИИ-звезды!',
                    url: videoUrl
                });
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Share failed:', error);
                }
            }
        }
    };

    // Callbacks for MainCard
    const handleGenerationStart = (persona) => {
        setIsGenerating(true);
        setGeneratingPersona(persona);
    };

    const handleGenerationComplete = (videoUrl) => {
        setIsGenerating(false);
        if (videoUrl) {
            setGeneratedVideo(videoUrl);
        }
    };

    const handleNoCredits = (persona, text) => {
        trackEvent('see_paywall');
        setPreSelectedPersona(persona);
        setPreSelectedText(text || '');
        setShowPaywall(true);
    };

    const handleOpenPaywall = () => {
        trackEvent('see_paywall');
        setShowPaywall(true);
    };

    const handleClosePaywall = () => {
        setShowPaywall(false);
        setPreSelectedPersona(null);
        setPreSelectedText('');
    };

    const handleClearPreSelection = () => {
        setPreSelectedPersona(null);
        setPreSelectedText('');
    };

    const handleRegenerate = async (item) => {
        try {
            await createVideoGeneration({
                celebId: item.celeb_id,
                wishText: item.wish_text,
                accountId: ACCOUNT_ID,
            });
            fetchAccountStatus();
        } catch (error) {
            console.error('Regenerate error:', error);
            showError('Ошибка при повторной генерации');
        }
    };

    const handlePurchaseSuccess = () => {
        fetchAccountStatus();
        setShowPaywall(false);
        setPreSelectedPersona(null);
        setPreSelectedText('');
        setShowPostPurchase(true);
    };

    const handlePayForItem = () => {
        // Single-item retries no longer supported as a separate flow — open the paywall.
        handleOpenPaywall();
    };

    // Video Result State
    if (generatedVideo) {
        return (
            <div className="min-h-screen bg-white font-sans flex items-center justify-center p-4">
                <div className="max-w-2xl w-full">
                    <h2 className="text-[42px] font-bold text-brand-dark text-center mb-8 leading-[1.1] tracking-[-0.42px]">Ваше
                        видео готово!</h2>
                    <div className="bg-white rounded-[30px] shadow-2xl overflow-hidden mb-6">
                        <video controls autoPlay className="w-full">
                            <source src={generatedVideo} type="video/mp4"/>
                        </video>
                    </div>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <a
                            href={generatedVideo}
                            download
                            className="px-8 py-4 bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white font-medium rounded-2xl hover:opacity-90 transition shadow-lg text-center"
                        >
                            Скачать видео
                        </a>
                        {typeof navigator !== 'undefined' && navigator.share && (
                            <button
                                onClick={() => handleShareVideo(generatedVideo)}
                                className="md:hidden px-8 py-4 bg-white text-brand-dark font-medium rounded-2xl hover:bg-gray-50 transition shadow-lg border border-gray-200"
                            >
                                Поделиться
                            </button>
                        )}
                        <button
                            onClick={() => {
                                setGeneratedVideo(null);
                            }}
                            className="px-8 py-4 bg-brand-gray text-brand-dark font-medium rounded-2xl hover:bg-gray-200 transition"
                        >
                            Создать ещё
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white font-sans overflow-x-hidden relative">

            <div className="relative z-10">
                <Header
                    onHomeClick={() => {
                        setPreSelectedPersona(null);
                        window.scrollTo({top: 0, behavior: 'smooth'});
                    }}
                />

                {isOrders ? (
                    <main className="pt-6">
                        <MyContentPage
                            history={history}
                            credits={credits}
                            subscription={subscription}
                            plans={plans}
                            linkedTelegramId={linkedTelegramId}
                            linkedMaxId={linkedMaxId}
                            onPlay={(url) => setPlayingVideo(url)}
                            onPay={handlePayForItem}
                            fetchAccountStatus={fetchAccountStatus}
                            onGenerateClick={() => {
                                window.location.href = '/';
                            }}
                            onBuyCredits={handleOpenPaywall}
                            onRegenerate={handleRegenerate}
                        />
                    </main>
                ) : (
                    <main className="pt-6">
                        <HeroSection config={pageConfig.hero}/>

                        <div ref={mainCardRef}>
                            <MainCard
                                credits={credits}
                                selectedPersonaId={selectedPersonaId}
                                onPersonaChange={setSelectedPersonaId}
                                onGenerationStart={handleGenerationStart}
                                onGenerationComplete={handleGenerationComplete}
                                onAccountStatusUpdate={fetchAccountStatus}
                                onNoCredits={handleNoCredits}
                                hideBack={false}
                                isExampleVideoPlaying={isExampleVideoPlaying}
                                onStopExampleVideo={() => stopExampleVideoRef.current?.()}
                            />
                        </div>

                        <WhyPerfectSection config={pageConfig.whyPerfect}/>

                        <FiveReasonsSection reasons={pageConfig.fiveReasons}/>

                        <StarReviewSection
                            personaId={selectedPersonaId || undefined}
                            personaName={selectedPersonaId ? PERSONAS.find(p => p.id === selectedPersonaId)?.name : undefined}
                            personaImage={selectedPersonaId ? PERSONAS.find(p => p.id === selectedPersonaId)?.image : undefined}
                        />

                        {/* Blog articles */}
                        <div className="max-w-[1110px] mx-auto px-4 pb-10 md:pb-16">
                            <h2 className="text-[28px] md:text-[42px] font-bold text-brand-dark leading-[1.1] tracking-[-0.42px] mb-8 text-center">
                                Полезные{' '}
                                <span className="text-gradient">статьи</span>
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {blogArticles.slice(0, 4).map(article => (
                                    <a
                                        key={article.slug}
                                        href={`/blog/${article.slug}/`}
                                        className="group block rounded-2xl border border-brand-dark/10 p-5 hover:border-brand-red/30 hover:shadow-md transition"
                                    >
                                        <h3 className="font-bold text-brand-dark group-hover:text-brand-red transition leading-snug">
                                            {article.title}
                                        </h3>
                                        <p className="text-sm text-brand-dark/60 mt-2 leading-relaxed">{article.excerpt}</p>
                                    </a>
                                ))}
                            </div>
                            <div className="text-center mt-6">
                                <a
                                    href="/blog/"
                                    className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-medium hover:opacity-95 transition"
                                >
                                    Все статьи блога
                                </a>
                            </div>
                        </div>
                    </main>
                )}

                <Footer/>
            </div>

            {/* Generation In Progress Floating Modal */}
            {isGenerating && (
                <div
                    className="fixed bottom-6 right-6 z-50 w-[320px] bg-white rounded-[20px] shadow-2xl overflow-hidden">
                    <div className="p-5">
                        <div className="flex items-center justify-between mb-4">
                            <span
                                className="text-lg font-medium text-brand-dark tracking-[-0.18px]">Генерируем...</span>
                            <button
                                onClick={() => setIsGenerating(false)}
                                className="w-8 h-8 rounded-full bg-brand-gray flex items-center justify-center text-brand-dark/60 hover:bg-gray-200 transition"
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2"
                                          strokeLinecap="round"/>
                                </svg>
                            </button>
                        </div>

                        <div className="relative rounded-[12px] overflow-hidden mb-4">
                            <img
                                src={generatingPersona?.image}
                                alt={generatingPersona?.name}
                                className="w-full h-[180px] object-cover"
                            />
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                <div
                                    className="w-12 h-12 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                            </div>
                        </div>

                        <p className="text-base font-medium text-brand-dark text-center leading-[1.3] tracking-[-0.16px] mb-4 whitespace-pre-line">
                            {GENERATION_STATUS_TEXTS[statusTextIndex]}
                        </p>
                    </div>
                </div>
            )}

            {/* Post-Purchase Suggestion Modal */}
            {showPostPurchase && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-[24px] p-8 max-w-md w-full shadow-2xl text-center">
                        <div
                            className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"
                                 strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17l-5-5"/>
                            </svg>
                        </div>

                        <h3 className="text-2xl font-bold text-brand-dark mb-2">
                            Оплата прошла!
                        </h3>
                        <p className="text-brand-dark/60 mb-6">
                            Кредиты зачислены на ваш баланс.
                            Создайте видеопоздравление прямо сейчас!
                        </p>

                        <button
                            onClick={() => {
                                setShowPostPurchase(false);
                                window.scrollTo({top: 0, behavior: 'smooth'});
                            }}
                            className="w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold hover:opacity-95 transition mb-3"
                        >
                            Создать видео
                        </button>

                        <a
                            href="/orders"
                            className="block w-full py-3 text-brand-dark/60 hover:text-brand-dark text-sm font-medium transition"
                        >
                            Перейти к заказам
                        </a>
                    </div>
                </div>
            )}

            <Paywall
                isOpen={showPaywall}
                onClose={handleClosePaywall}
                plans={plans}
                subscription={subscription}
                credits={credits}
                linkedTelegramId={linkedTelegramId}
                linkedMaxId={linkedMaxId}
                preSelectedPersona={preSelectedPersona}
                preSelectedText={preSelectedText}
                onPurchaseSuccess={handlePurchaseSuccess}
                onClearPreSelection={handleClearPreSelection}
            />

            <VideoModal videoUrl={playingVideo} onClose={() => setPlayingVideo(null)}/>
        </div>
    );
}
